/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import DecoupledEditorBase from "@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor";
import EditorWatchdog from "@ckeditor/ckeditor5-watchdog/src/editorwatchdog";

// Built in plugins
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import CloudServices from "@ckeditor/ckeditor5-cloud-services/src/cloudservices";
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment";
import BalloonToolbar from "@ckeditor/ckeditor5-ui/src/toolbar/balloon/balloontoolbar";
import ExportPdf from "@ckeditor/ckeditor5-export-pdf/src/exportpdf";
import Base64UploadAdapter from "@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter";

import ImportWord from "@ckeditor/ckeditor5-import-word/src/importword";
import FontSize from "@ckeditor/ckeditor5-font/src/fontsize";
import FontFamily from "@ckeditor/ckeditor5-font/src/fontfamily";
import FontColor from "@ckeditor/ckeditor5-font/src/fontcolor";
import FontBackgroundColor from "@ckeditor/ckeditor5-font/src/fontbackgroundcolor";
import UploadAdapter from "@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter";
import Comments from "@ckeditor/ckeditor5-comments/src/comments";
import Autoformat from "@ckeditor/ckeditor5-autoformat/src/autoformat";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Strikethrough from "@ckeditor/ckeditor5-basic-styles/src/strikethrough";
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline";
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote";
import CKFinder from "@ckeditor/ckeditor5-ckfinder/src/ckfinder";
import ListProperties from "@ckeditor/ckeditor5-list/src/listproperties";
import EasyImage from "@ckeditor/ckeditor5-easy-image/src/easyimage";
import GeneralHtmlSupport from "@ckeditor/ckeditor5-html-support/src/generalhtmlsupport";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import Image from "@ckeditor/ckeditor5-image/src/image";
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption";
import ImageStyle from "@ckeditor/ckeditor5-image/src/imagestyle";
import ImageToolbar from "@ckeditor/ckeditor5-image/src/imagetoolbar";
import ImageUpload from "@ckeditor/ckeditor5-image/src/imageupload";
import Indent from "@ckeditor/ckeditor5-indent/src/indent";
import IndentBlock from "@ckeditor/ckeditor5-indent/src/indentblock";
import Highlight from "@ckeditor/ckeditor5-highlight/src/highlight";
import HtmlEmbed from "@ckeditor/ckeditor5-html-embed/src/htmlembed";
import Link from "@ckeditor/ckeditor5-link/src/link";
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";
import Table from "@ckeditor/ckeditor5-table/src/table";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import TableProperties from "@ckeditor/ckeditor5-table/src/tableproperties";
import TableCellProperties from "@ckeditor/ckeditor5-table/src/tablecellproperties";
import TableColumnResize from "@ckeditor/ckeditor5-table/src/tablecolumnresize";
import TextTransformation from "@ckeditor/ckeditor5-typing/src/texttransformation";
import TodoList from "@ckeditor/ckeditor5-list/src/todolist";
import TrackChangesData from "@ckeditor/ckeditor5-track-changes/src/trackchangesdata";
import TrackChanges from "@ckeditor/ckeditor5-track-changes/src/trackchanges";
import RestrictedEditingMode from "@ckeditor/ckeditor5-restricted-editing/src/restrictededitingmode";
import StandardEditingMode from "@ckeditor/ckeditor5-restricted-editing/src/standardeditingmode";
import RevisionHistory from "@ckeditor/ckeditor5-revision-history/src/revisionhistory";
import CommentsRepository from "@ckeditor/ckeditor5-comments/src/comments/commentsrepository";
import RevisionsRepository from "@ckeditor/ckeditor5-revision-history/src/revisionsrepository";
import TrackChangesEditing from "@ckeditor/ckeditor5-track-changes/src/trackchangesediting";
import "./styles/styles.css";

// Custom plugins
import Autosave from "@ckeditor/ckeditor5-autosave/src/autosave";
import Clipboard from "@ckeditor/ckeditor5-clipboard/src/clipboard";
import Mention from "@ckeditor/ckeditor5-mention/src/mention";
import Pagination from "@ckeditor/ckeditor5-pagination/src/pagination";
import PageBreak from "@ckeditor/ckeditor5-page-break/src/pagebreak";
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize";
import FindAndReplace from "@ckeditor/ckeditor5-find-and-replace/src/findandreplace";
import List from "@ckeditor/ckeditor5-list/src/list";
import { AdjacentListsSupport } from "@ckeditor/ckeditor5-list";

// Our Views
import { getCustomCommentView } from "./extensions/view/customCommentView";
import { getCustomCommentThreadView } from "./extensions/view/customCommentThreadView";
import { getCustomSuggestionThreadView } from "./extensions/view/customSuggestionThreadView";

import PrivateThread from "./extensions/plugins/private-thread/privateThread";
import PublicThread from "./extensions/plugins/public-thread/publicThread";
import getCreatorPermissions from "./extensions/plugins/landlordPermissions/creatorPermissions";
import CommentsHide from "./extensions/plugins/commentsHide/commentsHide";
import HeaderFooterButton from "./extensions/plugins/createHeaderFooterButton/createHeaderFooterButton";
import getCustomAnnotationsUI from "./extensions/plugins/customAnnotationsUI/CustomAnnotationsUI";
import TrackChangesPermissions from "./extensions/plugins/trackChangesPermissions/trackChangesPermissions";
import { InsertPageNumber } from "./extensions/plugins/inlineInsertPageNumber/insertPageNumber";
import InsertNumberedList from "./extensions/plugins/abbreviationPlugin/insertNumberedList";
import InlineCheckbox from "./extensions/plugins/inlineCheckbox/inlineCheckbox";
import getPlaceholdersPlugin from "./extensions/plugins/placeholders/placeholders";
import FormattingOptions from "./extensions/plugins/formattingOptions/formattingOptions";

const configureDecoupledEditor = ({
  context,
  threadRendered,
  isCreator,
  documentReadOnly,
  permissions,
  isShared,
  userDocumentRole,
  documentStatus,
  enableRestrictedEditingMode,
  documentType,
}) => {
  class DecoupledEditor extends DecoupledEditorBase {}
  // Plugins to include in the build.
  DecoupledEditor.builtinPlugins = [
    Alignment,
    AdjacentListsSupport,
    InsertNumberedList,
    Autoformat,
    Autosave,
    BalloonToolbar,
    Base64UploadAdapter,
    BlockQuote,
    Bold,
    CloudServices,
    CKFinder,
    Clipboard,
    Comments,
    ListProperties,
    RevisionsRepository,
    CommentsRepository,
    TrackChangesEditing,
    EasyImage,
    Essentials,
    ExportPdf,
    ImportWord,
    FindAndReplace,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    Highlight,
    HtmlEmbed,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    InsertPageNumber,
    InlineCheckbox,
    Italic,
    Link,
    List,
    MediaEmbed,
    Mention,
    PageBreak,
    Pagination,
    Paragraph,
    PasteFromOffice,
    PrivateThread,
    PublicThread,
    ...(enableRestrictedEditingMode ? [RestrictedEditingMode] : []),
    RevisionHistory,
    StandardEditingMode,
    Strikethrough,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    TableColumnResize,
    TextTransformation,
    TodoList,
    TrackChangesData,
    TrackChanges,
    ...(documentStatus === "reviewing" ? [TrackChangesPermissions] : []),
    Underline,
    UploadAdapter,
    CommentsHide,
    HeaderFooterButton,
    getCreatorPermissions({ isCreator, permissions, isShared, documentStatus }),
    getCustomAnnotationsUI(),
    getPlaceholdersPlugin({ documentType }),
    FormattingOptions,
  ];

  DecoupledEditor.defaultConfig = {
    exportPdf: {
      dataCallback: (editor) => {
        return editor.getData({ showSuggestionHighlights: true });
      },
    },
    comments: {
      CommentView: getCustomCommentView(context, threadRendered),
      CommentThreadView: getCustomCommentThreadView(
        context,
        threadRendered,
        permissions,
        documentReadOnly,
        documentStatus
      ),
    },
    trackChanges: {
      SuggestionThreadView: getCustomSuggestionThreadView(
        isCreator,
        permissions.hasApprovePermission,
        userDocumentRole,
        documentStatus
      ),
    },
    removePlugins: [TodoList],
    image: {
      insert: {
        type: 'auto'
      },
      styles: [
        "alignLeft",
        "alignRight",
        "alignCenter",
        "alignBlockLeft",
        "alignBlockRight",
        "block",
        "inline",
        "side",
      ],
      toolbar: [
        "imageStyle:inline",
        "imageStyle:wrapText",
        "imageStyle:breakText",
      ],
    },
    // This value must be kept in sync with the language defined in webpack.config.js.
    language: "en",
  };

  return DecoupledEditor;
};

export default {
  getDecoupledEditor: configureDecoupledEditor,
  EditorWatchdog,
};
